import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DocumentNumberModeEnum, DocumentNumberModel, DocumentNumberPrefixSeparatorEnum, DocumentNumberPrefixTypeEnum } from '../../models';
import { TradeSettingsService } from '../../../trade/trade-settings.service';
import { CommonModalsActionsEnum, WarningModalComponent } from 'common/src/modules/modals/modals-common';

export interface ErrorMessage {
  documentType: 'GOODS'
    | 'SERVICES'
    | 'DIGITAL_PRODUCTS'
    | 'BUNDLE'
    | 'PRIVATE_PARTNERS'
    | 'CORPORATE_PARTNERS';
  itemId: number;
  level: 'ERROR';
  message: string;
  modal: boolean;
  name: string;
  priority: number;
}

@Component({
  selector: 'rnpl-product-partner-number-settings',
  templateUrl: './product-partner-number-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductPartnerNumberSettingsComponent implements OnChanges, OnDestroy {

  @Input() documentType: 'GOODS'
    | 'SERVICES'
    | 'DIGITAL_PRODUCTS'
    | 'BUNDLE'
    | 'PRIVATE_PARTNERS'
    | 'CORPORATE_PARTNERS';
  @Input() documentNumberSettings: DocumentNumberModel;
  @Input() serverErrors: ErrorMessage[];
  @Input() allowedContinueSequence: boolean = false;

  public form: FormGroup;
  public numberPreview: string;
  public documentNumberModeEnum = DocumentNumberModeEnum;
  public documentNumberPrefixTypeEnum = DocumentNumberPrefixTypeEnum;
  public valueMaxLength = 32;

  public title: {[key in string]?: string} = {
    ['GOODS']:  'COMMON.GOODS',
    ['SERVICES']:  'APP.SERVICES',
    ['DIGITAL_PRODUCTS']:  'APP.DIGITAL_PRODUCTS',
    ['BUNDLE']:  'BUNDLES.BUNDLES',
    ['PRIVATE_PARTNERS']:  'APP.PRIVATE_PARTNERS',
    ['CORPORATE_PARTNERS']:  'APP.CORPORATE_PARTNERS',
  };

  public prefixTypeList: { value: DocumentNumberModeEnum, label: string }[] = [
    {value: DocumentNumberModeEnum.SINGLE, label: 'DOCUMENTS_NUMBERS.SINGLE'},
    {value: DocumentNumberModeEnum.DOUBLE, label: 'DOCUMENTS_NUMBERS.DOUBLE'},
    // {value: DocumentNumberModeEnum.NONE, label:   'DOCUMENTS_NUMBERS.NONE'},
  ];

  public prefixList: { value: DocumentNumberPrefixTypeEnum, label: string }[] = [
    {value: DocumentNumberPrefixTypeEnum.ALPHA_SEQUENCE, label: 'RID_SETTINGS.ALPHABETICAL'},
    {value: DocumentNumberPrefixTypeEnum.NUMBER_SEQUENCE, label: 'RID_SETTINGS.NUMERICAL'},
    {value: DocumentNumberPrefixTypeEnum.USER_DEFINED, label: 'COMMON.USER_DEFINED'},
  ];

  public separatorList: { value: DocumentNumberPrefixSeparatorEnum, label: string }[] = [
    {value: DocumentNumberPrefixSeparatorEnum.SLASH, label: '/'},
    {value: DocumentNumberPrefixSeparatorEnum.DOT, label: '.'},
    {value: DocumentNumberPrefixSeparatorEnum.COLON, label: ':'},
    {value: DocumentNumberPrefixSeparatorEnum.DASH, label: '-'},
    {value: DocumentNumberPrefixSeparatorEnum.NONE, label: 'DOCUMENTS_NUMBERS.WITHOUT_SEPARATOR'},
  ];

  public sequenceLengthList: { value: number, label: string }[] = [
    { value: 1, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 1}) },
    { value: 2, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 2}) },
    { value: 3, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 3}) },
    { value: 4, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 4}) },
    { value: 5, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 5}) },
  ];

  public numberLengthList: { value: number, label: string }[] = [
    { value: 3, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 3}) },
    { value: 4, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 4}) },
    { value: 5, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 5}) },
    { value: 6, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 6}) },
    { value: 7, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 7}) },
    { value: 8, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 8}) },
    { value: 9, label: this.translateService.instant('DOCUMENTS_NUMBERS.N_SYMBOLS', {n: 9}) },
  ];

  @Output() resetEmit: EventEmitter<string> = new EventEmitter<string>();

  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private tradeSettingsService: TradeSettingsService,
  ) {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.hasOwnProperty('documentNumberSettings') && this.documentNumberSettings) {
        this.form.patchValue(this.documentNumberSettings.forwardSettings);
      }
      if (changes.hasOwnProperty('serverErrors') && this.serverErrors && this.serverErrors.length) {
        this.setServerErrors();
      }
    }
  }

  private getValueValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(this.valueMaxLength),
      // Validators.pattern('[a-zA-Z]*')
    ];
  }

  public initForm(): void  {
    this.form = this.fb.group({
      firstPrefix: this.fb.group({
        separator: [],
        type: [],
        sequenceLength: [],
        value: [null, this.getValueValidators()],
      }),
      secondPrefix: this.fb.group({
        separator: [],
        type: [],
        sequenceLength: [],
        value: [null, this.getValueValidators()],
      }),
      length: [],
      mode: [],
      continueExistingNumberSequence: [],
      number: [],
    });

    this.modeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode: DocumentNumberModeEnum) => {
        // if (mode === DocumentNumberModeEnum.NONE) {
        //   this.firstPrefixGroup.patchValue(this.documentNumberSettings.currentSettings.firstPrefix, {emitEvent: false});
        //   this.secondPrefixGroup.patchValue(this.documentNumberSettings.currentSettings.secondPrefix, {emitEvent: false});
        // }
        if (mode === DocumentNumberModeEnum.SINGLE) {
          this.secondPrefixGroup.patchValue(this.documentNumberSettings.currentSettings.secondPrefix, {emitEvent: false});
        }
      });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getRunpleIdPreview();

        if (this.allowedContinueSequence && this.continueExistingNumberSequenceControl.value) {
          this.numberControl.setValidators(Validators.required);
        } else {
          this.numberControl.clearValidators();
        }
        this.numberControl.updateValueAndValidity({emitEvent: false})
      });

    this.firstPrefixTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: DocumentNumberPrefixTypeEnum) => {
        this.firstPrefixValueControl.patchValue(null, { emitEvent: false });
        if (value === DocumentNumberPrefixTypeEnum.USER_DEFINED) {
          this.firstPrefixValueControl.enable({ emitEvent: false });
        } else {
          this.firstPrefixValueControl.disable({ emitEvent: false });
        }
      });

    this.secondPrefixTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: DocumentNumberPrefixTypeEnum) => {
        this.secondPrefixValueControl.patchValue(null, { emitEvent: false });
        if (value === DocumentNumberPrefixTypeEnum.USER_DEFINED) {
          this.secondPrefixValueControl.enable({ emitEvent: false });
        } else {
          this.secondPrefixValueControl.disable({ emitEvent: false });
        }
      });
  }

  private setServerErrors(): void {
    this.serverErrors
      .filter(err => err.documentType === this.documentType)
      .forEach(error => {
        const control: AbstractControl = this.form.get(error.name);
        if (control) {
          control.setErrors({backEndValidation: error.message});
        }
      });
  }

  public isValid(): boolean {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({emitEvent: false});

    return this.form.valid;
  }

  public getUpdated(): DocumentNumberModel {
    return {
      ...this.documentNumberSettings,
      forwardSettings: {
        ...this.documentNumberSettings.forwardSettings,
        ...this.form.getRawValue()
      }
    };
  }

  public reset(): void {
    const dialog = this.dialog.open(WarningModalComponent, {
      data: {
        title: 'DOCUMENTS_NUMBERS.RESET_TO_DEFAULTS_MODAL_TITLE',
        message: 'DOCUMENTS_NUMBERS.RESET_TO_DEFAULTS_MODAL_DESCR',
        confirmBtnText: 'BUTTON.CONTINUE',
        confirmBtnIcon: 'arrow-right'
      }
    });

    dialog.afterClosed().subscribe(res => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.resetEmit.emit(this.documentType);
      }
    })
  }

  public getRunpleIdPreview(): void {
    if (!this.documentType || !this.documentNumberSettings) { return; }

    this.tradeSettingsService.getNextRunpleIdDocumentNumberSettings(this.documentType, this.form.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe((numberPreview: string) => {
        this.numberPreview = numberPreview;
        this.cdr.detectChanges();
      });
  }

  get lengthControl(): FormControl { return this.form.get('length') as FormControl }
  get modeControl(): FormControl { return this.form.get('mode') as FormControl }
  get continueExistingNumberSequenceControl(): FormControl { return this.form.get('continueExistingNumberSequence') as FormControl }
  get numberControl(): FormControl { return this.form.get('number') as FormControl }

  get firstPrefixGroup(): FormGroup { return this.form.get('firstPrefix') as FormGroup; }
  get secondPrefixGroup(): FormGroup { return this.form.get('secondPrefix') as FormGroup; }

  get firstPrefixSeparatorControl(): FormControl { return this.firstPrefixGroup.get('separator') as FormControl }
  get firstPrefixTypeControl(): FormControl { return this.firstPrefixGroup.get('type') as FormControl }
  get firstPrefixValueControl(): FormControl { return this.firstPrefixGroup.get('value') as FormControl }
  get firstPrefixSequenceLengthControl(): FormControl { return this.firstPrefixGroup.get('sequenceLength') as FormControl }

  get secondPrefixSeparatorControl(): FormControl { return this.secondPrefixGroup.get('separator') as FormControl }
  get secondPrefixTypeControl(): FormControl { return this.secondPrefixGroup.get('type') as FormControl }
  get secondPrefixValueControl(): FormControl { return this.secondPrefixGroup.get('value') as FormControl }
  get secondPrefixSequenceLengthControl(): FormControl { return this.secondPrefixGroup.get('sequenceLength') as FormControl }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
